import React from 'react'
import { css } from '@emotion/core'
import { func } from 'prop-types'
import { useLocation, Link } from '@fs/zion-router'
import { boolAttr } from '@fs/zion-frontend-friends'
import { useContainerWidth, useOverlay, Ellipsis, colors } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeedBadge } from '../../../providers/FeedBadgeProvider'
import { useTopicTabs, useGetStreaks } from '../../../hooks'
import getIsTWAMode from '../../../helpers/appMode_helper'
import { MenuStories, MenuFeed, MenuTemple, MenuActivities, MenuMore } from '../icons'
import LeaderboardOverlay from '../../../components/LeaderboardOverlay'
import StreaksOverlay from '../../../components/Streaks/StreaksOverlay'
import { Z_INDICES } from '../../../constants/zIndices'
import { navEnterAnimationCss } from '../../../helpers/enterAnimation'
import useEnterAnimationRun from '../../../hooks/useEnterAnimationRun'

const tabsCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  &[is-tablet],
  &[is-mobile] {
    position: fixed;
    bottom: 0;
    z-index: ${Z_INDICES.NAV_TABS};
    width: 100vw;
    background-color: ${colors.gray00};
    height: 75px;
    box-shadow: 0 5px 15px ${colors.gray100};
    a {
      width: 18vw;
    }
    a::after {
      background: none;
    }
  }
  &[is-mobile] {
    a {
      padding: 0;
    }
  }
  & a {
    line-height: 20px;
    font-size: 12px;
    color: ${colors.transparent.gray70};
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1vw;
    text-transform: none;
    transition:
      color 0.2s ease,
      width 0.2s ease,
      opacity 0.1s ease-out;
    position: relative;
    margin: 0 1vw;
    margin-bottom: 10px;
    &::after {
      content: '';
      height: 4px;
      width: 0;
      background: ${colors.blue80};
      opacity: 0.5;
      position: absolute;
      bottom: -3px;
      border-radius: 8px;
      inset-inline-start: 0;
      inset-inline-end: 0;
      margin: 0 auto;
      transition:
        width 0.2s ease,
        opacity 0.2s ease;
    }
  }

  a:hover,
  a:focus {
    color: ${colors.blue80};
    background: none;
    &::after {
      width: 50%;
    }
  }
  & a[active] {
    border: none;
    color: ${colors.blue80};
    font-weight: 600;
    &::after {
      width: 100%;
      opacity: 1;
    }
  }
`

const tabPressCss = css`
  & a:active {
    opacity: 0.6;
  }
`

export default function NavTabs({ onClick }) {
  const [t] = useTranslation()
  const { badgeCount, setFeedIconRef } = useFeedBadge()
  const location = useLocation()
  const atWidth = useContainerWidth()
  const isTablet = atWidth({ sm: true, lg: false, default: false })
  const isMobile = atWidth({ sm: false, default: true })
  const topics = useTopicTabs()
  const isTWA = getIsTWAMode()
  const enterRun = useEnterAnimationRun(isTWA)
  const streaksOverlay = useOverlay()
  const pointsOverlay = useOverlay()

  const { data: getStreaksState } = useGetStreaks(true)
  const endOfStreak = getStreaksState?.endOfWeek
  const totalActivities = getStreaksState?.totalActivities ?? 0
  const streakCount = getStreaksState?.streakCount ?? 0
  const isStreakCurrent = getStreaksState?.isCurrent ?? false

  const menuFontSize = atWidth({ default: '10px', sm: '12px' })
  const tabStyles = css`
    font-size: ${menuFontSize};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `

  const getIcon = (type, selected) => {
    switch (type) {
      case 'stories':
        return <MenuStories selected={selected} />
      case 'feed':
        return (
          <div ref={setFeedIconRef} style={{ position: 'relative' }}>
            <MenuFeed selected={selected} />
            {badgeCount > 0 && (
              <div
                css={css`
                  position: absolute;
                  top: -4px;
                  right: -4px;
                  background-color: ${colors.danger60};
                  color: white;
                  border-radius: 50%;
                  width: 16px;
                  height: 16px;
                  font-size: 10px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  pointer-events: none;
                `}
              >
                {badgeCount}
              </div>
            )}
          </div>
        )
      case 'temple':
        return <MenuTemple selected={selected} />
      case 'activities':
        return <MenuActivities selected={selected} />
      default:
        return <MenuMore selected={selected} />
    }
  }

  return (
    <>
      <nav
        key={`nav_${enterRun}`}
        aria-label={t('navtabs.aria-label', 'Family Connect Navigation Tabs')}
        css={[tabsCss, isTWA && tabPressCss, isTWA && navEnterAnimationCss]}
        is-tablet={boolAttr(isTablet)}
        is-mobile={boolAttr(isMobile)}
      >
        {topics?.map((topicData) => {
          const isCurrentPath = topicData.paths.includes(location.pathname)
          const isChildPath = topicData?.childrenPaths?.includes(location.pathname)
          const selected = isCurrentPath || isChildPath

          return (
            <Link
              key={topicData?.testId}
              noLinkStyles
              onClick={() => onClick(topicData)}
              active={boolAttr(selected)}
              aria-current={selected ? 'page' : 'false'}
              data-testid={topicData.testId}
              to={topicData.paths[0]}
              css={tabStyles}
            >
              {getIcon(topicData?.type, selected)}
              <Ellipsis>{t(topicData?.title)}</Ellipsis>
            </Link>
          )
        })}
      </nav>

      <LeaderboardOverlay overlay={pointsOverlay} />
      <StreaksOverlay
        streakCount={streakCount}
        endOfStreak={endOfStreak}
        overlay={streaksOverlay}
        totalActivities={totalActivities}
        isCurrent={isStreakCurrent}
      />
    </>
  )
}

NavTabs.propTypes = {
  onClick: func,
}
